import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { UserService } from './user.service';

export interface NavigationTab_Dto {
    moduleName: string;
    tabName: string;
    componentRoute: string;
    sortOrder: number;
}

export interface NavigationConfig_Dto {
    // Role the slice was fetched for — used to invalidate the cache when the
    // session role changes (admin impersonation via backupSession/restoreBackupSession,
    // or a re-login in the same tab).
    role: string;
    routes: string[];
    tabs: NavigationTab_Dto[];
}

/**
 * Role-scoped navigation config (allowed routes + module tabs), served by
 * /api/MenuHandler/GetNavigationConfig. Replaces the static ValidRouts.ts /
 * tabData.ts bundle data, which exposed every role's permission matrix in the
 * served JS (VAPT). The API resolves role and SPID from the JWT claims, so a
 * user can only ever obtain their own role's slice.
 *
 * IN-MEMORY CACHE ONLY (user-requested 2026-08-21): nothing is written to
 * sessionStorage/localStorage, so the config is not visible in DevTools →
 * Application — same storage model as the sidebar menu (ReduxApiHandlerService's
 * NgRx in-memory cache). Consequence: the cache dies on a hard refresh / new
 * tab and the API is re-called once per page load — exactly like the sidebar.
 * The in-flight shareReplay ensures the guard, tab bar and any parallel
 * navigations still trigger at most ONE request per load.
 */
@Injectable({
    providedIn: 'root'
})
export class NavigationConfigService {

    private apiUrl = environment.apiUrl;
    private config: NavigationConfig_Dto | null = null;
    private inFlight$: Observable<NavigationConfig_Dto> | null = null;

    constructor(private http: HttpClient, private userService: UserService) { }

    getConfig(): Observable<NavigationConfig_Dto> {
        const role = this.userService.getRole();

        if (this.config && this.config.role === role) {
            return of(this.config);
        }
        this.config = null;

        // GetNavigationConfig is [Authorize]. Firing it before the access token has been
        // stored — the window between a login response arriving and the session being
        // written, or any caller that runs on an unauthenticated screen — sends an empty
        // Bearer and gets a 401. The auth interceptor then attempts a refresh, and with no
        // refresh token either that ends in forceLogout(), wiping the session the user was
        // in the middle of establishing. Fail fast instead of making that request.
        const accessToken = this.userService.getAccessToken();
        if (!accessToken) {
            return throwError(() => new Error('No access token available for navigation config'));
        }

        if (!this.inFlight$) {
            const headers = new HttpHeaders({
                'Authorization': `Bearer ${accessToken}`
            });
            const url = `${this.apiUrl}/api/MenuHandler/GetNavigationConfig`;
            this.inFlight$ = this.http.get<any>(url, { headers }).pipe(
                map(body => ({
                    role,
                    routes: body?.routes ?? [],
                    tabs: body?.tabs ?? []
                }) as NavigationConfig_Dto),
                tap(cfg => {
                    this.config = cfg;
                    this.inFlight$ = null;
                }),
                shareReplay(1),
                catchError(err => {
                    this.inFlight$ = null;
                    return throwError(() => err);
                })
            );
        }
        return this.inFlight$;
    }

    /** Cache-only read for synchronous consumers (e.g. analytics). Null until first fetch. */
    getConfigSync(): NavigationConfig_Dto | null {
        const role = this.userService.getRole();
        return this.config && this.config.role === role ? this.config : null;
    }

    clearCache(): void {
        this.config = null;
        this.inFlight$ = null;
    }
}
